// ============================================================================
// BESPOKIAN Health - HIPAA Compliance Tracking
// Progress checklist toward HIPAA compliance (same style as HIPAA Notice page)
// ============================================================================

import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Shield, Check, Square } from 'lucide-react';

/** Checklist item: done = implemented; !done = still required */
const checklist = [
  {
    category: 'Infrastructure (Terraform / AWS)',
    items: [
      { done: true, label: 'Secrets in AWS Secrets Manager (no secrets in repo)' },
      { done: true, label: 'RDS in private subnets, not publicly accessible' },
      { done: true, label: 'VPC and NAT for ECS/RDS' },
      { done: false, label: 'RDS encryption at rest (storage_encrypted / KMS)' },
      { done: false, label: 'HTTPS enforced in production (TLS only; no HTTP default)' },
      { done: false, label: 'KMS for encryption keys where required' },
      { done: false, label: 'Audit log retention 6+ years (e.g. CloudWatch or export)' },
      { done: false, label: 'WAF / DDoS mitigation where required' },
      { done: false, label: 'AWS BAA in place (organizational)' },
    ],
  },
  {
    category: 'Backend / Application',
    items: [
      { done: true, label: 'PHI access audit logging (patients, prescriptions, auth)' },
      { done: true, label: 'Audit log hash chain for tamper detection' },
      { done: true, label: 'HIPAA routes (phi-access, accounting of disclosures)' },
      { done: true, label: 'Argon2id for passwords; integer cents for money' },
      { done: false, label: 'All audit writes use AuditService (e.g. reporting aligned)' },
      { done: false, label: 'Encryption at rest for DB (via infra)' },
      { done: false, label: 'Breach notification workflow implemented/documented' },
      { done: false, label: 'Session timeout / auto-logout enforced' },
      { done: false, label: 'RBAC documented and enforced on every PHI endpoint' },
    ],
  },
  {
    category: 'Legal / Policy',
    items: [
      { done: true, label: 'HIPAA Notice of Privacy Practices published (/hipaa)' },
      { done: true, label: 'Privacy Policy references HIPAA and breach (72h)' },
      { done: false, label: 'Business Associate Agreements (BAA) with AWS and vendors' },
      { done: false, label: 'Risk assessment / security risk analysis documented' },
      { done: false, label: 'Workforce HIPAA training program documented' },
    ],
  },
  {
    category: 'Operations',
    items: [
      { done: false, label: 'Breach response playbook' },
      { done: false, label: 'Regular access reviews' },
      { done: false, label: 'Penetration testing / vulnerability scans' },
      { done: false, label: 'Backup and restore tested' },
    ],
  },
];

export function HIPAATrackingPage() {
  const total = checklist.reduce((acc, c) => acc + c.items.length, 0);
  const done = checklist.reduce((acc, c) => acc + c.items.filter((i) => i.done).length, 0);
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        {/* Hero — same style as HIPAA.tsx */}
        <section className="bg-gradient-to-br from-navy-50 via-white to-sage-50/30 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />

            <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-4">
              Compliance
            </p>

            <h1 className="font-display text-4xl md:text-5xl text-navy-800 tracking-tight leading-tight mb-6 italic">
              HIPAA Compliance Tracking
            </h1>

            <p className="text-xl text-navy-600 max-w-2xl mx-auto">
              Progress toward end-to-end HIPAA compliance. We are not yet fully compliant; additional steps are required.
            </p>

            <p className="text-sm text-navy-400 mt-4">
              {done} of {total} items complete ({pct}%)
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-6 bg-white border-b border-navy-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-navy-800 mb-2">Notice</h2>
                  <p className="text-navy-600 text-sm">
                    This page tracks our progress toward HIPAA compliance. Completion of all items does not by itself constitute legal compliance; 
                    a formal risk assessment and legal review are required. See our{' '}
                    <a href="/hipaa" className="text-gold-600 hover:text-gold-700 font-medium">HIPAA Notice</a> for context.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Checklist by category */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {checklist.map((group) => (
                <div key={group.category} className="bg-navy-50 rounded-xl p-6 border border-navy-100">
                  <h2 className="text-lg font-semibold text-navy-800 mb-4">{group.category}</h2>
                  <ul className="space-y-3">
                    {group.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="flex-shrink-0 mt-0.5 text-navy-600" aria-hidden>
                          {item.done ? (
                            <Check className="h-5 w-5 text-sage-600" aria-label="Done" />
                          ) : (
                            <Square className="h-5 w-5 text-navy-400" aria-label="Not done" />
                          )}
                        </span>
                        <span
                          className={
                            item.done
                              ? 'text-navy-600 text-sm leading-relaxed'
                              : 'text-navy-700 text-sm leading-relaxed'
                          }
                        >
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
